import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../api/services/auth';
import { useToast } from '../hooks/useToast';
import { Text, Button, Card, Container, Input } from '../components/common';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });

      if (response.success) {
        showToast('Password updated successfully', 'success');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setError(response.message || 'Failed to update password');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <Container>
        <Card>
          <Text variant="p" className="text-red-500 dark:text-red-400">
            User not found
          </Text>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card padding="large">
        <div className="mb-8">
          <Text variant="h1">Settings</Text>
          <Text variant="p" className="mt-2 text-sm">
            Manage your account settings and preferences
          </Text>
        </div>

        {/* Password Change Section */}
        <div className="bg-gray-50 dark:bg-gray-700 p-8 rounded-lg shadow-sm">
          <div className="max-w-2xl">
            <Text variant="h2" className="mb-6">Change Password</Text>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="password"
                id="currentPassword"
                name="currentPassword"
                label="Current Password"
                value={formData.currentPassword}
                onChange={handleInputChange}
                required
                placeholder="Enter your current password"
              />

              <Input
                type="password"
                id="newPassword"
                name="newPassword"
                label="New Password"
                value={formData.newPassword}
                onChange={handleInputChange}
                required
                placeholder="Enter your new password"
              />

              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm New Password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                placeholder="Confirm your new password"
              />

              {error && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                  <Text variant="p" className="text-red-600 dark:text-red-400">{error}</Text>
                </div>
              )}

              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={isLoading}
                  fullWidth
                  className="mt-4 py-4"
                >
                  Update Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Card>
    </Container>
  );
};

export default Settings;